/**
 * Geodata — CDN-backed country/region data.
 *
 * Fetches slim JSON from Vercel Blob CDN on demand:
 *   - countries.json (~10KB) — fetched once on first getCountries() call
 *   - regions.json   (~95KB) — fetched lazily on first getStatesForCountry() call
 *
 * Both are cached in memory after first fetch.
 */

const GEODATA_CDN_BASE = 'https://cvwnizdbugpz6jwk.public.blob.vercel-storage.com/geodata/v1';

export const AVAILABLE_LANGUAGES = ['en', 'ru', 'de', 'da', 'fr', 'es', 'zh', 'hi', 'pt', 'ja', 'ar', 'it', 'he'] as const;
export type SupportedLanguage = (typeof AVAILABLE_LANGUAGES)[number];

export interface Country {
  code: string;
  name: string;
  iso3?: string;
  numeric?: number;
}

export interface State {
  code: string;
  name: string;
  countryCode: string;
}

interface CdnCountries { [iso2: string]: { n: string; i3?: string; nu?: number } }
interface CdnRegions   { [iso2: string]: [string, string][] }

const countriesCache = new Map<SupportedLanguage, CdnCountries>();
const regionsCache   = new Map<SupportedLanguage, CdnRegions>();
const inflightCountries = new Map<SupportedLanguage, Promise<CdnCountries>>();
const inflightRegions   = new Map<SupportedLanguage, Promise<CdnRegions>>();

async function fetchCountriesRaw(language: SupportedLanguage): Promise<CdnCountries> {
  const cached = countriesCache.get(language);
  if (cached) return cached;

  let inflight = inflightCountries.get(language);
  if (inflight) return inflight;

  inflight = (async () => {
    const url = `${GEODATA_CDN_BASE}/${language}/countries.json`;
    const res = await fetch(url);
    if (!res.ok) throw new Error(`Geodata fetch failed: ${url} (${res.status})`);
    const data: CdnCountries = await res.json();
    countriesCache.set(language, data);
    inflightCountries.delete(language);
    return data;
  })();

  inflightCountries.set(language, inflight);
  return inflight;
}

async function fetchRegionsRaw(language: SupportedLanguage): Promise<CdnRegions> {
  const cached = regionsCache.get(language);
  if (cached) return cached;

  let inflight = inflightRegions.get(language);
  if (inflight) return inflight;

  inflight = (async () => {
    const url = `${GEODATA_CDN_BASE}/${language}/regions.json`;
    const res = await fetch(url);
    if (!res.ok) throw new Error(`Geodata fetch failed: ${url} (${res.status})`);
    const data: CdnRegions = await res.json();
    regionsCache.set(language, data);
    inflightRegions.delete(language);
    return data;
  })();

  inflightRegions.set(language, inflight);
  return inflight;
}

function getCountriesCached(language: SupportedLanguage): CdnCountries {
  return countriesCache.get(language) || countriesCache.get('en') || {};
}

function getRegionsCached(language: SupportedLanguage): CdnRegions {
  return regionsCache.get(language) || regionsCache.get('en') || {};
}

/** Fetch countries + regions from CDN for a language. Call before sync getters. */
export async function ensureGeoDataLoaded(language: SupportedLanguage = 'en'): Promise<void> {
  await Promise.all([fetchCountriesRaw(language), fetchRegionsRaw(language)]);
}

export const getCountries = (language: SupportedLanguage = 'en'): Country[] => {
  const raw = getCountriesCached(language);
  return Object.entries(raw)
    .map(([code, c]) => ({ code, name: c.n, iso3: c.i3, numeric: c.nu }))
    .sort((a, b) => a.name.localeCompare(b.name));
};

export const getStatesForCountry = (countryCode: string, language: SupportedLanguage = 'en'): State[] => {
  const raw = getRegionsCached(language);
  const tuples = raw[countryCode];
  if (!tuples) return [];
  return tuples
    .map(([iso, name]) => ({ code: iso, name, countryCode }))
    .sort((a, b) => a.name.localeCompare(b.name));
};

export function isLanguageLoaded(language: SupportedLanguage): boolean {
  return countriesCache.has(language);
}
